import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {ImageService} from "../../service/image.service";
import {SelectedProducts} from "../../models/selectedProducts";
import {ShippingType} from "../../enums/ShippingType";
import {SharedConstants} from "../../utilityClass/sharedConstants";
import {PromoCodeService} from "../../service/promo-code.service";
import {
  PROMO_ALREADY_USED_ERROR_MESSAGE,
  PROMO_CODE_IS_EXPIRED_MESSAGE,
  PROMO_CODE_IS_INVALID_MESSAGE,
  PROMO_LIMIT_EXPIRATION_ERROR_MESSAGE,
  PROMO_CODE_NOT_FOUND,PROMO_CODE_INVALID_PATTERN_MESSAGE,
  PROMO_CODE_EXPIRED, INVALID_PROMO_CODE_MESSAGE, PROMO_CODE_IS_REQUIRED
} from "../../constants/promo-code.constant";
import {DiscountResponse} from "../../interface/discountResponse";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {HttpErrorResponse} from "@angular/common/http";
import {PromoCodeUtil} from "../../service/util/promo-code-util.service";
import {ShoppingSharedService} from "../../service/shopping-shared.service";
import {TranslateService} from "@ngx-translate/core";
import {Language} from "../../models/language";
import {LANG} from "../../constants/local-storage.contant";
import {CookieUtils} from "../../utils/cookie.utils";

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrl: './order-summary.component.scss'
})
export class OrderSummaryComponent {
  @Input() allCarts: SelectedProducts[] = [];
  @Input() totalSumUsd: number = 0;
  @Input() totalSumAmd: number = 0;
  @Input() calculatedSumUsd: number = 0;
  @Input() calculatedSumAmd: number = 0;
  @Input() payment: boolean = false;
  @Input() deliveryMethod: ShippingType = ShippingType.STANDARD;

  shippingForm: FormGroup;
  isContentVisible: boolean = true;

  isPromoCodeResponseInvalid: boolean = false;
  isPromoCodeExpired: boolean = false;
  isPromoCodeEmpty: boolean = false;
  promoCodeControl: FormControl<string | null> = new FormControl<string | null>('');
  @Input() promoCodeErrorMessage: boolean = false;
  @Output() applyPromoCode: EventEmitter<DiscountResponse> = new EventEmitter<DiscountResponse>();

  constructor(
    private formBuilder: FormBuilder,
    private imageService: ImageService,
    private translateService: TranslateService,
    private promoCodeService: PromoCodeService,
    private snackBar: SnackBarUtil,
    private shoppingSharedService: ShoppingSharedService
  ) {
    this.shippingForm = this.buildShippingForm();
    this.translateService.use(CookieUtils.getCookie(LANG) || Language.HY);
    this.addPromoCodeValidators();
  }

  get getPromoCodeErrorMessage() {
    if (this.isPromoCodeEmpty) {
      return this.translateService.instant(PROMO_CODE_IS_REQUIRED);
    } else if (this.isPromoCodeResponseInvalid) {
      return this.translateService.instant(PROMO_CODE_IS_INVALID_MESSAGE);
    } else if (this.isPromoCodeExpired) {
      return this.translateService.instant(PROMO_CODE_IS_EXPIRED_MESSAGE);
    }
    return "";
  }

  toggleContentVisibility() {
    this.isContentVisible = !this.isContentVisible;
  }

  onApplyPromoCode() {
    this.promoCodeControl.markAsTouched();

    if (this.promoCodeControl.invalid) {
      if (this.promoCodeControl.errors?.['required']) {
        this.isPromoCodeEmpty = true;
      }
      return;
    }

    if (this.promoCodeControl.valid) {
      this.promoCodeService.discountByPrice(this.totalSumAmd, this.promoCodeControl.value as string).subscribe({
        next: (response: DiscountResponse) => {
          this.shoppingSharedService.promoCode = this.promoCodeControl.value as string;
          this.applyPromoCode.emit(response);
          const message = this.translateService.instant('promo_code_mes.product_success_promo_apply_message');
          this.showSnackbar(MockupDataModel.success(message), SnackBarUtil.CLASSES.success);
        },
        error: (error: HttpErrorResponse) => {
          if (PromoCodeUtil.isPromoCodeNotFound(error)) {
            this.promoCodeControl.setErrors({ [PROMO_CODE_NOT_FOUND]: true });
            this.isPromoCodeResponseInvalid = true;
          } else if (PromoCodeUtil.isPromoCodeLimitExpired(error)) {
            this.showSnackbar(MockupDataModel.error(PROMO_LIMIT_EXPIRATION_ERROR_MESSAGE), SnackBarUtil.CLASSES.error);
          } else if (PromoCodeUtil.isPromoCodeAlreadyUsed(error)) {
            this.showSnackbar(MockupDataModel.error(this.translateService.instant('payment.promo_already_used')), SnackBarUtil.CLASSES.error);
          } else if (PromoCodeUtil.isPromoCodeExpired(error)) {
            this.promoCodeControl.setErrors({ [PROMO_CODE_EXPIRED]: true })
            this.isPromoCodeExpired = true;
          } else if (PromoCodeUtil.isPromoCodeInvalid(error)) {
            this.showSnackbar(
              MockupDataModel.error(this.translateService.instant(INVALID_PROMO_CODE_MESSAGE)),
              SnackBarUtil.CLASSES.error
            );
          }
        }
      })
    }
  }

  private buildShippingForm(): FormGroup {
    return this.formBuilder.group({
      promoCode: ['', [Validators.required]],
    });
  }

  getProductImageUrl(imageName: string) {
    return this.imageService.getImageUrl(imageName)
  }

  private addPromoCodeValidators() {
    this.promoCodeControl.addValidators([
      Validators.required
    ]);
  }

  get getPromoCodeErrorMessageByValidatorName() {
    if (this.isPromoCodeResponseInvalid || this.isPromoCodeExpired || this.isPromoCodeEmpty) {
      return this.getPromoCodeErrorMessage;
    }
    const invalidValidatorName = this.getPromoCodeInvalidValidatorName();
    switch (invalidValidatorName) {
      case SharedConstants.EMPTY:
        return this.translateService.instant(PROMO_CODE_IS_REQUIRED);
      case SharedConstants.PATTERN:
        return this.translateService.instant(PROMO_CODE_INVALID_PATTERN_MESSAGE);
      default:
        return '';
    }
  }

  private getPromoCodeInvalidValidatorName(): string | null {
    const isTouched = this.promoCodeControl.touched || this.promoCodeControl.dirty;
    if (this.promoCodeControl && this.promoCodeControl.errors && isTouched) {
      return Object.keys(this.promoCodeControl.errors)[0];
    }
    return null;
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  protected readonly ShippingType = ShippingType;
}
